import styles from './QuickSearch.less';

import { connect } from 'react-redux';
import chunk from 'chunk';
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import {
  ChevronIcon,
  Text,
  TextLink,
  Columns,
  PageBlock,
  Hidden
} from 'seek-style-guide/react';
import QuickSearchLink from './QuickSearchLink/QuickSearchLink';

import { quickSearchViewModel } from '@seek/seek-refine-job-search';

import {
  quickSearchClassificationClicked,
  quickSearchCityClicked,
  quickSearchGraduateClicked
} from 'src/store/search/search';

const AUcities = [
  {
    name: 'Sydney',
    query: { where: 'All Sydney NSW' }
  },
  {
    name: 'Melbourne',
    query: { where: 'All Melbourne VIC' }
  },
  {
    name: 'Brisbane',
    query: { where: 'All Brisbane QLD' }
  },
  {
    name: 'Gold Coast',
    query: { where: 'All Gold Coast QLD' }
  },
  {
    name: 'Perth',
    query: { where: 'All Perth WA' }
  },
  {
    name: 'Adelaide',
    query: { where: 'All Adelaide SA' }
  },
  {
    name: 'Hobart',
    query: { where: 'All Hobart TAS' }
  },
  {
    name: 'Darwin',
    query: { where: 'All Darwin NT' }
  },
  {
    name: 'Canberra',
    query: { where: 'All Canberra ACT' }
  }
];

const NZcities = [
  {
    name: 'Auckland',
    query: { where: 'All Auckland' }
  },
  {
    name: 'Hamilton',
    query: { where: 'Hamilton Waikato' }
  },
  {
    name: 'Tauranga',
    query: { where: 'Tauranga Bay of Plenty' }
  },
  {
    name: 'Wellington',
    query: { where: 'All Wellington' }
  },
  {
    name: 'Christchurch',
    query: { where: 'Christchurch Canterbury' }
  },
  {
    name: 'Dunedin',
    query: { where: 'Dunedin Otago' }
  }
];

const {
  classifications: {
    priority: priorityClassifications,
    all: allClassifications
  }
} = quickSearchViewModel();

const columnLength = Math.ceil(allClassifications.length / 3);
const classificationColumns = chunk(allClassifications, columnLength);

function mapStateToProps({ location }) {
  return {
    locale: location.locale
  };
}

const mapDispatchToProps = {
  onClassificationClick: quickSearchClassificationClicked,
  onCityClick: quickSearchCityClicked,
  onGraduateClick: quickSearchGraduateClicked
};

class QuickSearch extends Component {
  static propTypes = {
    locale: PropTypes.string.isRequired,
    onClassificationClick: PropTypes.func.isRequired,
    onCityClick: PropTypes.func.isRequired,
    onGraduateClick: PropTypes.func.isRequired
  };

  shouldComponentUpdate(nextProps) {
    return this.props.locale !== nextProps.locale;
  }

  render() {
    const {
      locale,
      onClassificationClick,
      onCityClick,
      onGraduateClick
    } = this.props;
    const isAU = locale === 'AU';
    const isNZ = locale === 'NZ';
    const cities = isAU ? AUcities : NZcities;

    return (
      <Hidden
        mobile
        component="section"
        className={styles.root}
        data-automation="quick-search-container"
      >
        <PageBlock>
          <Text small strong>
            <h1>Quick search</h1>
          </Text>

          <div className={styles.row}>
            <Text small className={styles.rowHeading}>
              <h2>Classifications</h2>
            </Text>
            {priorityClassifications.map(({ description, searchQuery }) => (
              <QuickSearchLink
                key={description}
                linkProps={{
                  location: {
                    pathname: '/jobs',
                    query: searchQuery
                  },
                  'aria-label': `Jobs in ${description}`,
                  analytics: 'quick-search-classification-priority',
                  onClick: onClassificationClick,
                  'data-automation': 'quick-search-priority-classification-link'
                }}
              >
                {description}
              </QuickSearchLink>
            ))}

            <input
              id="allClassificationsToggle"
              type="checkbox"
              className={styles.toggle}
            />
            <label
              htmlFor="allClassificationsToggle"
              className={styles.toggleLabel}
            >
              <Text small>
                <TextLink>
                  View all
                  <ChevronIcon
                    direction="down"
                    className={styles.chevron}
                    svgClassName={styles.chevronSvg}
                  />
                </TextLink>
              </Text>
            </label>
            <div className={styles.toggleContent}>
              <Columns>
                {classificationColumns.map((column, i) => (
                  <span key={i}>
                    {column.map(({ description, searchQuery }) => (
                      <QuickSearchLink
                        key={description}
                        linkProps={{
                          location: {
                            pathname: '/jobs',
                            query: searchQuery
                          },
                          'aria-label': `Jobs in ${description}`,
                          analytics: 'quick-search-classification',
                          onClick: onClassificationClick,
                          'data-automation': 'quick-search-classification-link'
                        }}
                      >
                        {description}
                      </QuickSearchLink>
                    ))}
                  </span>
                ))}
              </Columns>
            </div>
          </div>

          <div className={styles.row}>
            <Text small className={styles.rowHeading}>
              <h2>Major cities</h2>
            </Text>
            {cities.map(({ name, query }) => (
              <QuickSearchLink
                key={`Jobs in ${name}`}
                linkProps={{
                  location: {
                    pathname: '/jobs',
                    query
                  },
                  'aria-label': `Jobs in ${name}`,
                  analytics: 'quick-search-city',
                  onClick: onCityClick,
                  'data-automation': 'quick-search-city-link'
                }}
              >
                {name}
              </QuickSearchLink>
            ))}
          </div>

          <div className={styles.row}>
            <Text small className={styles.rowHeading}>
              <h2>Other</h2>
            </Text>
            {(isAU && [
              <QuickSearchLink
                key="graduate"
                linkProps={{
                  location: {
                    pathname: '/jobs',
                    query: { graduatesearch: 'true' }
                  },
                  'aria-label': 'Graduate / Entry Level jobs',
                  analytics: 'quick-search-graduate',
                  onClick: onGraduateClick,
                  'data-automation': 'quick-search-graduate-link'
                }}
              >
                Graduate / Entry Level
              </QuickSearchLink>,
              <QuickSearchLink
                key="volunteer"
                linkProps={{
                  location: 'https://www.volunteer.com.au/volunteering',
                  'data-automation': 'quick-search-volunteer-link'
                }}
              >
                Volunteer
              </QuickSearchLink>,
              <QuickSearchLink
                key="recruiter"
                linkProps={{
                  location: '/Recruiter',
                  rel: 'nofollow',
                  'aria-label': 'Search by recruiter',
                  analytics: 'quick-search-recruiter',
                  'data-automation': 'quick-search-recruiters-link'
                }}
              >
                Search by Recruiter
              </QuickSearchLink>
            ]) ||
              (isNZ && [
                <QuickSearchLink
                  key="high-pay"
                  linkProps={{
                    location: '/100kjobs',
                    'aria-label': 'Jobs paying more than $100k',
                    analytics: 'quick-search-100kjobs',
                    'data-automation': 'quick-search-100kjobs-link'
                  }}
                >
                  Jobs paying more than $100k
                </QuickSearchLink>,
                <QuickSearchLink
                  key="employers"
                  linkProps={{
                    location: '/Employer',
                    rel: 'nofollow',
                    'aria-label': 'Search By Employers',
                    analytics: 'quick-search-employers',
                    'data-automation': 'quick-search-employer-link'
                  }}
                >
                  Search by Employers
                </QuickSearchLink>
              ])}
          </div>
        </PageBlock>
      </Hidden>
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(QuickSearch);
